import { LinkProvider, type MapTalesTheme, Provider } from '@fontbold/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  HeadContent,
  Scripts,
} from '@tanstack/react-router';
import { AppCookieConsent } from '#/components/AppCookieConsent';
import { RouterLink } from '#/components/RouterLink';
import { cookieConsent } from '#/lib/consent';
import { initSentry } from '#/lib/sentry';
import { siteTitle } from '#/lib/site';
import {
  FONTBOLD_THEME,
  fontPreloads,
  fontsCss,
  toProviderTheme,
} from '#/lib/site-theme';
import { getSpace } from '#/lib/space-fn';
import { DEFAULT_LOGO, spaceLogo } from '#/lib/space-logo';
import type { RouterContext } from '../router';
import appCss from '../styles.css?url';

if (typeof window !== 'undefined' && cookieConsent.isAllowed('analytics')) {
  initSentry();
}

type RootLoaderData = {
  title: string;
  lang: string;
  favicon: string;
  theme: MapTalesTheme;
  fontPreloads: string[];
  fontsCss: string;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  loader: async (): Promise<RootLoaderData> => {
    const space = await getSpace();
    const theme = space?.theme ?? FONTBOLD_THEME;
    return {
      title: siteTitle(space),
      lang: space?.locale ?? 'nl',
      favicon: space ? spaceLogo(space) : DEFAULT_LOGO,
      theme: toProviderTheme(theme),
      fontPreloads: fontPreloads(theme),
      fontsCss: fontsCss(theme),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: loaderData?.title ?? siteTitle(null) },
    ],
    links: [
      { rel: 'icon', href: loaderData?.favicon ?? DEFAULT_LOGO },
      ...(loaderData?.fontPreloads ?? fontPreloads(FONTBOLD_THEME)).map(
        (href) => ({
          rel: 'preload',
          href,
          as: 'font',
          type: 'font/woff2',
          crossOrigin: 'anonymous' as const,
        }),
      ),
      { rel: 'stylesheet', href: appCss },
    ],
    styles: [{ children: loaderData?.fontsCss ?? fontsCss(FONTBOLD_THEME) }],
  }),
  shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  const { queryClient } = Route.useRouteContext();
  const theme =
    Route.useLoaderData({ select: (data) => data.theme }) ?? undefined;
  const lang = Route.useLoaderData({ select: (data) => data.lang }) ?? 'nl';

  return (
    <html lang={lang}>
      <head>
        <HeadContent />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <Provider theme={theme}>
            <LinkProvider component={RouterLink}>
              {children}
              <AppCookieConsent />
            </LinkProvider>
          </Provider>
        </QueryClientProvider>
        <Scripts />
      </body>
    </html>
  );
}
